import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import React from "react";
import { Link, useParams, createFileRoute } from "@tanstack/react-router";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";

import DocumentTextField from "../../components/DocumentTextField";
import { ResendEmailVerificationInput, resendEmailVerification, resendEmailVerificationSchemaInput } from "../../api/emailVerification";

type States = "success" | "error" | "resend_success";

interface ContentITem {
  gap: number;
  title: React.JSX.Element;
  messages: React.JSX.Element[];
  action?: React.JSX.Element;
  warning?: React.JSX.Element;
}

export function EmailValidation(): React.JSX.Element {
  const { t } = useTranslation();
  const { id } = useParams({ from: "/__public/email-validate/$id" });
  const [state, setState] = React.useState<States>("success");
  const methods = useForm<ResendEmailVerificationInput>({
    defaultValues: { document: "" },
    resolver: yupResolver(resendEmailVerificationSchemaInput),
    criteriaMode: "all",
    mode: "all",
  });
  const { control } = methods;

  React.useEffect(() => {
    (async () => {
      const res = await fetch(`/api/v1/auth/verify-email/${id}`);
      setState(res.ok ? "success" : "error");
    })();
  }, [id]);

  const content: Record<States, ContentITem> = {
    success: {
      gap: 2,
      title: (
        <Typography color="success.main" variant="h4">
          {t("emailValidation.accountReady")}
        </Typography>
      ),
      messages: [
        <Typography key="1">{t("emailValidation.emailMainChannel")}</Typography>,
        <Typography key="2">{t("emailValidation.checkInbox")}</Typography>,
      ],
      action: (
        <Button component={Link} size="large" to="/login" variant="contained">
          {t("emailValidation.enterPortal")}
        </Button>
      ),
    },
    error: {
      gap: 3,
      title: (
        <div>
          <Typography color="warning.main" variant="h4">
            {t("emailValidation.validateEmail")}
          </Typography>
          <Typography variant="body2">{t("emailValidation.requestNewLink")}</Typography>
        </div>
      ),
      messages: [
        <Typography key="1">{t("emailValidation.emailNotValidated")}</Typography>,
        <Typography key="2">{t("emailValidation.indispensableStep")}</Typography>,
      ],
      action: (
        <>
          <Controller
            control={control}
            name="document"
            render={({ field, fieldState: { error } }) => (
              <DocumentTextField
                {...field}
                error={Boolean(error)}
                fullWidth
                helperText={error ? error.message : t("common.numbersOnly")}
                name="document"
                variant="outlined"
              />
            )}
          />
          <Button name="intent" size="large" type="submit" value="resend_success">
            {t("emailValidation.resendButton")}
          </Button>
        </>
      ),
      warning: (
        <Typography color="warning.main">{t("emailValidation.linkExpires")}</Typography>
      ),
    },
    resend_success: {
      gap: 2,
      title: (
        <div>
          <Typography color="success.main" variant="h4">
            {t("emailValidation.linkSentSuccess")}
          </Typography>
          <Typography variant="body2">{t("emailValidation.almostThere")}</Typography>
        </div>
      ),
      messages: [
        <Typography key="1">{t("emailValidation.accessEmailToValidate")}</Typography>,
      ],
      warning: (
        <Typography color="warning.main">{t("emailValidation.linkExpires")}</Typography>
      ),
    },
  };

  const selectedContent = content[state];
  return (
    <section>
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          const form = e.currentTarget as HTMLFormElement;
          const formData = new FormData(form);
          const input: ResendEmailVerificationInput = { document: String(formData.get("document") || "") };
          const responseData = await resendEmailVerification(input);
          setState(responseData.status === 200 ? "resend_success" : "error");
        }}
      >
        <Stack gap={selectedContent.gap}>
          {selectedContent.title}
          {selectedContent.messages}
          {selectedContent.action}
          {selectedContent.warning}
        </Stack>
      </form>
    </section>
  );
}

export const Route = createFileRoute("/__public/email-validate/$id")({
  component: EmailValidation,
});
